<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import PlusIcon from '@lucide/svelte/icons/plus'
  import SearchIcon from '@lucide/svelte/icons/search'
  import SlidersHorizontalIcon from '@lucide/svelte/icons/sliders-horizontal'
  import XIcon from '@lucide/svelte/icons/x'
  import ChevronLeftIcon from '@lucide/svelte/icons/chevron-left'
  import ChevronRightIcon from '@lucide/svelte/icons/chevron-right'
  import MapPinIcon from '@lucide/svelte/icons/map-pin'
  import PencilIcon from '@lucide/svelte/icons/pencil'
  import TrashIcon from '@lucide/svelte/icons/trash-2'
  import ArrowRightIcon from '@lucide/svelte/icons/arrow-right'

  let {
    property_list = [],
    total = 0,
    total_properties = 0,
    page = 1,
    per_page = 20,
    all_cities = [],
    all_states = [],
    filters = {}
  } = $props()

  // Local form state (mirrors filter params) — initialised once from prop, intentional.
  // @svelte-ignore state_referenced_locally
  let searchQuery = $state(filters.q ?? '')
  // @svelte-ignore state_referenced_locally
  let filterCity = $state(filters.city ?? '')
  // @svelte-ignore state_referenced_locally
  let filterState = $state(filters.state ?? '')
  // @svelte-ignore state_referenced_locally
  let filterPostcode = $state(filters.postcode ?? '')
  // @svelte-ignore state_referenced_locally
  let filterOp = $state(filters.filter_op ?? 'and')
  let filterPanelOpen = $state(false)

  const showSearchFilter = $derived(total_properties >= 10)
  const showPagination = $derived(total > per_page)
  const totalPages = $derived(Math.max(Math.ceil(total / per_page), 1))

  const activeFilters = $derived(() => {
    const chips = []
    if (filters.q) chips.push({ key: 'q', label: `"${filters.q}"` })
    if (filters.city) chips.push({ key: 'city', label: `City: ${filters.city}` })
    if (filters.state) chips.push({ key: 'state', label: `State: ${filters.state}` })
    if (filters.postcode) chips.push({ key: 'postcode', label: `Postcode: ${filters.postcode}` })
    return chips
  })

  function visit(params) {
    router.visit('/properties', {
      data: params,
      preserveState: true,
      preserveScroll: true
    })
  }

  function submitSearch(e) {
    e?.preventDefault()
    visit(buildParams({ q: searchQuery, page: 1 }))
  }

  function applyFilters() {
    filterPanelOpen = false
    visit(buildParams({ city: filterCity, state: filterState, postcode: filterPostcode, filter_op: filterOp, q: searchQuery, page: 1 }))
  }

  function removeFilter(key) {
    const next = { q: filters.q, city: filters.city, state: filters.state, postcode: filters.postcode, filter_op: filters.filter_op, page: 1 }
    next[key] = ''
    // sync local state too
    if (key === 'q') searchQuery = ''
    if (key === 'city') filterCity = ''
    if (key === 'state') filterState = ''
    if (key === 'postcode') filterPostcode = ''
    visit(buildParams(next))
  }

  function clearAllFilters() {
    searchQuery = ''
    filterCity = ''
    filterState = ''
    filterPostcode = ''
    filterOp = 'and'
    visit({ page: 1 })
  }

  function goToPage(p) {
    visit(buildParams({ page: p }))
  }

  function buildParams(overrides = {}) {
    return {
      q: filters.q ?? '',
      city: filters.city ?? '',
      state: filters.state ?? '',
      postcode: filters.postcode ?? '',
      filter_op: filters.filter_op ?? 'and',
      page: page,
      ...overrides
    }
  }

  function deleteProperty(id, name) {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return
    router.delete(`/properties/${id}`)
  }

  function wizardStepUrl(propertyId, step) {
    const urls = {
      1: `/properties/${propertyId}/rooms`,
      2: `/properties/${propertyId}/property_finance`,
      3: `/properties/${propertyId}/statutory_taxes`,
      4: `/properties/${propertyId}/utilities`
    }
    return urls[step] ?? `/properties/${propertyId}/rooms`
  }

  function formatCents(cents) {
    if (cents == null) return null
    return 'RM ' + (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
</script>

<svelte:head>
  <title>My Properties — MyRentalTax</title>
</svelte:head>

<div class="p-4 sm:p-6 flex flex-col gap-4 sm:gap-5 flex-1 max-w-3xl mx-auto w-full">

  <!-- Page header -->
  <div class="flex items-center justify-between gap-3">
    <div>
      <h1 class="text-xl font-bold text-zinc-900 m-0">My Properties</h1>
      <p class="text-sm text-zinc-500 m-0 mt-0.5">{total_properties} {total_properties === 1 ? 'property' : 'properties'}</p>
    </div>
    <Button size="sm" onclick={() => router.visit('/properties/new')}>
      <PlusIcon class="w-4 h-4" />
      Add Property
    </Button>
  </div>

  <!-- Search + Filter (only shown when >= 10 properties) -->
  {#if showSearchFilter}
    <div class="flex flex-col gap-2">
      <!-- Search bar row -->
      <div class="flex gap-2">
        <form class="flex-1 relative" onsubmit={submitSearch}>
          <SearchIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search by name, address, city…"
            bind:value={searchQuery}
            class="w-full h-9 pl-9 pr-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </form>
        <button
          class="h-9 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md border transition-colors {filterPanelOpen ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'}"
          onclick={() => { filterPanelOpen = !filterPanelOpen }}
          aria-label="Toggle filters"
        >
          <SlidersHorizontalIcon class="w-4 h-4" />
          <span class="hidden sm:inline">Filters</span>
        </button>
      </div>

      <!-- Filter panel -->
      {#if filterPanelOpen}
        <div class="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col gap-4 shadow-sm">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <!-- City -->
            <div class="flex flex-col gap-1.5">
              <label for="filter-city" class="text-xs font-medium text-zinc-600">City</label>
              {#if all_cities.length > 0}
                <select
                  id="filter-city"
                  bind:value={filterCity}
                  class="h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                >
                  <option value="">All cities</option>
                  {#each all_cities as city}
                    <option value={city}>{city}</option>
                  {/each}
                </select>
              {:else}
                <input
                  id="filter-city"
                  type="text"
                  placeholder="e.g. Kuala Lumpur"
                  bind:value={filterCity}
                  class="h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
              {/if}
            </div>

            <!-- State -->
            <div class="flex flex-col gap-1.5">
              <label for="filter-state" class="text-xs font-medium text-zinc-600">State</label>
              {#if all_states.length > 0}
                <select
                  id="filter-state"
                  bind:value={filterState}
                  class="h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                >
                  <option value="">All states</option>
                  {#each all_states as state}
                    <option value={state}>{state}</option>
                  {/each}
                </select>
              {:else}
                <input
                  id="filter-state"
                  type="text"
                  placeholder="e.g. Selangor"
                  bind:value={filterState}
                  class="h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
              {/if}
            </div>

            <!-- Postcode -->
            <div class="flex flex-col gap-1.5">
              <label for="filter-postcode" class="text-xs font-medium text-zinc-600">Postcode</label>
              <input
                id="filter-postcode"
                type="text"
                placeholder="e.g. 47810"
                bind:value={filterPostcode}
                class="h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900"
              />
            </div>
          </div>

          <!-- AND/OR toggle + Apply -->
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <div class="flex items-center gap-2">
              <span class="text-xs text-zinc-500">Match:</span>
              <div class="flex rounded-md border border-zinc-200 overflow-hidden text-xs">
                <button
                  class="px-3 py-1.5 font-medium transition-colors {filterOp === 'and' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}"
                  onclick={() => { filterOp = 'and' }}
                >
                  All (AND)
                </button>
                <button
                  class="px-3 py-1.5 font-medium transition-colors border-l border-zinc-200 {filterOp === 'or' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}"
                  onclick={() => { filterOp = 'or' }}
                >
                  Any (OR)
                </button>
              </div>
            </div>
            <div class="flex gap-2">
              <button
                class="h-8 px-3 text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
                onclick={() => { filterCity = ''; filterState = ''; filterPostcode = '' }}
              >
                Reset
              </button>
              <Button size="sm" onclick={applyFilters}>Apply Filters</Button>
            </div>
          </div>
        </div>
      {/if}

      <!-- Active filter chips -->
      {#if activeFilters().length > 0}
        <div class="flex flex-wrap gap-1.5 items-center">
          <span class="text-xs text-zinc-400">Active:</span>
          {#each activeFilters() as chip (chip.key)}
            <span class="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-700 px-2 py-1 rounded-full font-medium">
              {chip.label}
              <button
                class="text-zinc-400 hover:text-zinc-700 transition-colors ml-0.5"
                onclick={() => removeFilter(chip.key)}
                aria-label="Remove filter"
              >
                <XIcon class="w-3 h-3" />
              </button>
            </span>
          {/each}
          <button class="text-xs text-zinc-400 hover:text-zinc-600 underline underline-offset-2 transition-colors" onclick={clearAllFilters}>
            Clear all
          </button>
        </div>
      {/if}

      <!-- Results count (when filtered) -->
      {#if filters.q || filters.city || filters.state || filters.postcode}
        <p class="text-xs text-zinc-400 m-0">
          {total} {total === 1 ? 'result' : 'results'} of {total_properties}
        </p>
      {/if}
    </div>
  {/if}

  <!-- Property list -->
  {#if property_list.length === 0}
    <div class="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <div class="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center">
        <SearchIcon class="w-5 h-5 text-zinc-400" />
      </div>
      <div>
        <p class="text-sm font-medium text-zinc-700 m-0">No properties found</p>
        <p class="text-xs text-zinc-400 m-0 mt-1">Try adjusting your search or filters</p>
      </div>
      <button class="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700 transition-colors" onclick={clearAllFilters}>
        Clear filters
      </button>
    </div>
  {:else}
    <div class="flex flex-col gap-3">
      {#each property_list as p (p.id)}
        <div class="bg-white border border-zinc-200 rounded-xl overflow-hidden hover:border-zinc-300 transition-colors">
          <!-- Card body -->
          <div class="px-4 pt-4 pb-3 flex flex-col gap-1">
            <div class="flex items-start justify-between gap-2">
              <span class="text-[15px] font-semibold text-zinc-900 leading-snug">{p.name}</span>
              <!-- Actions -->
              <div class="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="icon-sm" onclick={() => router.visit(`/properties/${p.id}/edit`)} aria-label="Edit">
                  <PencilIcon class="w-[15px] h-[15px]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  class="text-zinc-400 hover:bg-red-50 hover:text-red-600"
                  onclick={() => deleteProperty(p.id, p.name)}
                  aria-label="Delete"
                >
                  <TrashIcon class="w-[15px] h-[15px]" />
                </Button>
              </div>
            </div>
            {#if p.street_address || p.city || p.state || p.postcode}
              <div class="flex items-center gap-1.5 text-xs text-zinc-400">
                <MapPinIcon class="w-3 h-3 flex-shrink-0" />
                <span>{[p.street_address, p.city, p.state, p.postcode].filter(Boolean).join(', ')}</span>
              </div>
            {/if}
          </div>

          <!-- Income rows -->
          <div class="border-t border-zinc-100 px-4 py-3 flex flex-col gap-2">
            <div class="flex items-center justify-between text-[13px]">
              <span class="text-zinc-500">Rental Income</span>
              {#if p.rental_income_cents != null}
                <span class="font-medium text-zinc-900">{formatCents(p.rental_income_cents)}</span>
              {:else}
                <span class="text-zinc-300">—</span>
              {/if}
            </div>
            <div class="flex items-center justify-between text-[13px]">
              <span class="text-zinc-500">Statutory Income</span>
              {#if p.statutory_income_cents != null}
                <span class="font-medium text-zinc-900">{formatCents(p.statutory_income_cents)}</span>
              {:else}
                <span class="text-zinc-300">—</span>
              {/if}
            </div>
          </div>

          <!-- Footer CTA -->
          <div class="border-t border-zinc-100 px-4 py-3 flex items-center justify-end gap-2">
            <div class="flex justify-end">
              {#if p.wizard_complete}
                <Button size="sm" variant="outline" class="text-xs h-8 gap-1.5" onclick={() => router.visit(`/properties/${p.id}/summary`)}>
                  View Report
                  <ArrowRightIcon class="w-3.5 h-3.5" />
                </Button>
              {:else if p.last_wizard_step > 0}
                <Button size="sm" variant="outline" class="text-xs h-8 gap-1.5" onclick={() => router.visit(wizardStepUrl(p.id, p.last_wizard_step))}>
                  Continue Wizard
                  <ArrowRightIcon class="w-3.5 h-3.5" />
                </Button>
              {:else}
                <Button size="sm" class="text-xs h-8 gap-1.5" onclick={() => router.visit(`/properties/${p.id}/rooms`)}>
                  Start Wizard
                  <ArrowRightIcon class="w-3.5 h-3.5" />
                </Button>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>

    <!-- Pagination -->
    {#if showPagination}
      <div class="flex items-center justify-between pt-1">
        <span class="text-xs text-zinc-400">
          Page {page} of {totalPages} · {total} {total === 1 ? 'property' : 'properties'}
        </span>
        <div class="flex items-center gap-1">
          <button
            class="h-8 w-8 flex items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            onclick={() => goToPage(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeftIcon class="w-4 h-4" />
          </button>
          <button
            class="h-8 w-8 flex items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            onclick={() => goToPage(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            <ChevronRightIcon class="w-4 h-4" />
          </button>
        </div>
      </div>
    {/if}
  {/if}
</div>
